import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Buildings, IdentificationCard, FileArrowUp, CheckCircle, ArrowRight, ArrowLeft } from "@phosphor-icons/react";
import { AgreementAcceptPanel } from "../../components/legal/AgreementAcceptPanel";

// M3 — permanent Sender company onboarding with review-before-acceptance. PAN and GST are read from
// the document, shown READ-ONLY for review, and only accepted after the Sender explicitly confirms.
// Uploading/extraction alone never accepts an identifier or advances the flow. The only correction
// path is Replace document — PAN and GSTIN are never manually editable. The confirm request re-sends
// the same file so the accepted value is always freshly document-derived (never client-typed).
const ACCEPT = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";

function errMsg(e, fallback) {
  return e?.response?.data?.detail?.message || e?.response?.data?.detail || fallback;
}

export default function CompanyOnboarding() {
  const { profile, loading, refreshProfile } = useAuth();
  const nav = useNavigate();
  const [flow, setFlow] = useState(null);
  const [step, setStep] = useState("pan"); // pan | gst | details | agreement (NEW company only)
  const [agreement, setAgreement] = useState(null);
  const [agreementLoading, setAgreementLoading] = useState(false);
  const [agreementError, setAgreementError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expired, setExpired] = useState(false);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  // Client-side pending review state (never persisted server-side until confirmed).
  const [panReview, setPanReview] = useState(null); // {pan_display, pan_holder_name}
  const [gstReview, setGstReview] = useState(null); // {gstin_display, pan_matches}
  const panInput = useRef(null);
  const gstInput = useRef(null);

  const applyFlow = useCallback((f) => {
    setFlow(f);
    setName(f.company_name || "");
    setAddress(f.company_address || "");
  }, []);

  const boot = useCallback(async () => {
    try {
      let { data } = await api.get("/onboarding/current");
      if (!data || data.status === "NONE") {
        ({ data } = await api.post("/onboarding/start"));
      }
      applyFlow(data);
      setPanReview(data.pending_pan || null);
      setGstReview(data.pending_gst || null);
      if (data.pending_pan) setStep("pan");
      else setStep(data.pan_display ? "gst" : "pan");
    } catch (e) {
      if (e?.response?.status === 409) { await refreshProfile?.(); nav("/sender", { replace: true }); return; }
      toast.error(errMsg(e, "We couldn't start company setup. Please try again."));
    }
  }, [applyFlow, nav, refreshProfile]);

  useEffect(() => { if (profile?.role === "SENDER" && profile?.onboarding_required) boot(); }, [profile, boot]);

  if (loading) return <div className="p-10 mono text-sm" data-testid="onboarding-loading">Loading…</div>;
  if (!profile) return <div className="p-10 mono text-sm">Provisioning your profile…</div>;
  if (profile.role !== "SENDER") return <Navigate to="/app" replace />;
  if (!profile.onboarding_required) return <Navigate to="/sender" replace />;

  const handleExpired = (e) => {
    if (e?.response?.status === 410) { setExpired(true); return true; }
    return false;
  };

  const restart = async () => {
    setExpired(false); setFlow(null); setStep("pan");
    setPanReview(null); setGstReview(null);
    await boot();
  };

  // ---- PAN: review (single read) → confirm (no re-read) ----
  const reviewPan = async (file) => {
    if (!file || !flow) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/onboarding/${flow.flow_id}/pan`, fd);
      setPanReview({ pan_display: data.pan_display, pan_holder_name: data.pan_holder_name });
    } catch (e) { if (!handleExpired(e)) toast.error(errMsg(e, "We couldn’t read the PAN from this document. Upload a clear PAN card or e-PAN.")); }
    finally { setBusy(false); if (panInput.current) panInput.current.value = ""; }
  };

  const confirmPan = async () => {
    if (!panReview) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/onboarding/${flow.flow_id}/pan/confirm`);
      applyFlow(data);
      setPanReview(null);
      toast.success("PAN confirmed.");
    } catch (e) { if (!handleExpired(e)) toast.error(errMsg(e, "Couldn’t confirm the PAN. Please review and try again.")); }
    finally { setBusy(false); }
  };

  const cancelPanReview = async () => {
    setPanReview(null);
    try { await api.post(`/onboarding/${flow.flow_id}/pan/cancel`); } catch (e) { /* best-effort */ }
  };

  // ---- GST: review (single read) → confirm (no re-read) ----
  const reviewGst = async (file) => {
    if (!file || !flow) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/onboarding/${flow.flow_id}/gst`, fd);
      setGstReview({ gstin_display: data.gstin_display, pan_matches: data.pan_matches });
    } catch (e) { if (!handleExpired(e)) toast.error(errMsg(e, "We couldn’t read a GSTIN from this document. Upload the official GST Registration Certificate.")); }
    finally { setBusy(false); if (gstInput.current) gstInput.current.value = ""; }
  };

  const confirmGst = async () => {
    if (!gstReview) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/onboarding/${flow.flow_id}/gst/confirm`);
      applyFlow(data);
      setGstReview(null);
      toast.success("GSTIN confirmed.");
    } catch (e) { if (!handleExpired(e)) toast.error(errMsg(e, "Couldn’t confirm the GSTIN. Please review and try again.")); }
    finally { setBusy(false); }
  };

  const cancelGstReview = async () => {
    setGstReview(null);
    try {
      const { data } = await api.post(`/onboarding/${flow.flow_id}/gst/cancel`);
      applyFlow(data);
    } catch (e) { /* best-effort */ }
  };

  const removeGst = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/onboarding/${flow.flow_id}/gst`);
      applyFlow(data);
    } catch (e) { if (!handleExpired(e)) toast.error(errMsg(e, "We couldn't save your changes. Please try again.")); }
    finally { setBusy(false); }
  };

  const continueWithoutGst = async () => { await cancelGstReview(); setStep("details"); };

  const saveDetails = async () => {
    const { data } = await api.patch(`/onboarding/${flow.flow_id}/details`,
      { company_name: name.trim(), company_address: address.trim() });
    applyFlow(data);
    return data;
  };

  const done = async (message) => {
    toast.success(message);
    await refreshProfile?.();
    nav("/sender", { replace: true });
  };

  const loadAgreement = async () => {
    setAgreementLoading(true); setAgreementError(null); setAgreement(null);
    try {
      const { data } = await api.get(`/onboarding/${flow.flow_id}/agreement`);
      setAgreement(data);
    } catch (e) {
      if (!handleExpired(e)) setAgreementError(errMsg(e, "We couldn’t load the Air12 Sender Agreement."));
    } finally { setAgreementLoading(false); }
  };

  // Join an existing company (no Agreement step) — or, for a NEW company, continue to the Agreement.
  const finalize = async () => {
    setBusy(true);
    try {
      if (!flow.pan_exists) {
        if (!name.trim() || !address.trim()) { toast.error("Company name and address are required."); setBusy(false); return; }
        await saveDetails();
        setStep("agreement");
        setBusy(false);
        await loadAgreement();
        return;
      }
      const { data } = await api.post(`/onboarding/${flow.flow_id}/finalize`);
      await done(data.message);
    } catch (e) { if (!handleExpired(e)) toast.error(errMsg(e, "Couldn’t complete setup. Please try again.")); setBusy(false); }
  };

  // NEW company — accepting the Sender Agreement is the final, all-or-nothing onboarding step.
  const acceptAgreement = async (acceptingName) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/onboarding/${flow.flow_id}/finalize`, {
        agreement_version_id: agreement.version_id,
        authority_affirmed: true,
        accepting_person_name: acceptingName,
      }, { timeout: 90000 });
      await done(data.message);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "ALREADY_IN_COMPANY") { await done("Company setup complete."); return; }
      if (handleExpired(e)) return;
      if (code === "AGREEMENT_VERSION_STALE") {
        toast.error("The Air12 Sender Agreement has been updated. Please review and accept the latest version.");
        await loadAgreement();
      } else {
        toast.error(errMsg(e, "Your company setup was not completed. Please retry."));
      }
      setBusy(false);
    }
  };

  if (expired) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-5" data-testid="onboarding-expired">
        <div className="grid-card p-8 max-w-md text-center">
          <h1 className="heading text-2xl tracking-tight">Session expired</h1>
          <p className="text-sm text-[hsl(var(--muted))] mt-2">This setup session has expired. Start again.</p>
          <button className="btn-primary w-full justify-center mt-6" onClick={restart} data-testid="onboarding-restart-button">
            Start again
          </button>
        </div>
      </div>
    );
  }

  const panConfirmed = !!flow?.pan_display;
  const gstConfirmed = !!flow?.gst_present;

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[hsl(var(--background))] flex items-start justify-center px-5 py-12" data-testid="company-onboarding">
      <div className="w-full max-w-xl">
        <div className="overline mb-2">COMPANY SETUP</div>
        <h1 className="text-3xl sm:text-4xl heading tracking-tight leading-[1.05] mb-2">Set up your company</h1>
        <div className="flex gap-2 text-[11px] overline mb-7" data-testid="onboarding-steps">
          <span className={step === "pan" ? "text-[hsl(var(--accent))]" : ""}>1 · PAN</span>
          <span className={step === "gst" ? "text-[hsl(var(--accent))]" : ""}>2 · GST</span>
          <span className={step === "details" ? "text-[hsl(var(--accent))]" : ""}>3 · REVIEW</span>
          {!flow?.pan_exists && <span className={step === "agreement" ? "text-[hsl(var(--accent))]" : ""}>4 · AGREEMENT</span>}
        </div>

        {/* STEP 1 — PAN */}
        {step === "pan" && (
          <div className="grid-card p-6" data-testid="onboarding-step-pan">
            <div className="flex items-start gap-3 mb-4">
              <IdentificationCard size={22} weight="bold" className="text-[hsl(var(--accent))]" />
              <div>
                <h2 className="heading text-lg tracking-tight">Upload your PAN</h2>
                <p className="text-sm text-[hsl(var(--muted))] mt-1">PAN card or e-PAN. PDF, JPG or PNG up to 5 MB.</p>
              </div>
            </div>

            {/* Confirmed PAN */}
            {panConfirmed && !panReview ? (
              <div className="space-y-3">
                <div className="border rounded-[2px] p-4" data-testid="pan-confirmed">
                  <div className="overline text-[10px]">PAN · CONFIRMED (READ-ONLY)</div>
                  <div className="font-mono text-lg tracking-wide" data-testid="pan-value">{flow.pan_display}</div>
                  <div className="overline text-[10px] mt-3">PAN-HOLDER / ENTITY NAME</div>
                  <div className="text-sm" data-testid="pan-name">{flow.pan_holder_name}</div>
                </div>
                <div className="flex gap-2">
                  <button className="btn-ghost" disabled={busy} onClick={() => panInput.current?.click()} data-testid="pan-replace-button">
                    <FileArrowUp size={14} weight="bold" /> Replace document
                  </button>
                  <button className="btn-primary ml-auto" disabled={busy} onClick={() => setStep("gst")} data-testid="pan-continue-button">
                    Continue <ArrowRight size={14} weight="bold" />
                  </button>
                </div>
              </div>
            ) : panReview ? (
              /* Pending PAN review — read-only, awaiting explicit confirmation */
              <div className="space-y-3" data-testid="pan-review">
                <div className="border rounded-[2px] p-4">
                  <div className="overline text-[10px]">EXTRACTED PAN · REVIEW (READ-ONLY)</div>
                  <div className="font-mono text-lg tracking-wide" data-testid="pan-review-value">{panReview.pan_display}</div>
                  <div className="overline text-[10px] mt-3">PAN-HOLDER / ENTITY NAME</div>
                  <div className="text-sm" data-testid="pan-review-name">{panReview.pan_holder_name}</div>
                  <p className="text-[11px] text-[hsl(var(--muted))] mt-3">If this is wrong, choose Replace document. PAN cannot be edited by hand.</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button className="btn-ghost" disabled={busy} onClick={() => panInput.current?.click()} data-testid="pan-replace-button">
                    <FileArrowUp size={14} weight="bold" /> Replace document
                  </button>
                  <button className="btn-ghost" disabled={busy} onClick={cancelPanReview} data-testid="pan-cancel-button">Cancel</button>
                  <button className="btn-primary ml-auto" disabled={busy} onClick={confirmPan} data-testid="pan-confirm-button">
                    <CheckCircle size={14} weight="bold" /> {busy ? "Working…" : "Confirm PAN"}
                  </button>
                </div>
              </div>
            ) : (
              <button className="btn-primary w-full justify-center" disabled={busy} onClick={() => panInput.current?.click()} data-testid="pan-upload-button">
                <FileArrowUp size={16} weight="bold" /> {busy ? "Processing…" : "Upload PAN document"}
              </button>
            )}
            <input ref={panInput} type="file" accept={ACCEPT} className="hidden"
                   onChange={(e) => reviewPan(e.target.files?.[0])} data-testid="pan-file-input" />
          </div>
        )}

        {/* STEP 2 — GST (optional) */}
        {step === "gst" && (
          <div className="grid-card p-6" data-testid="onboarding-step-gst">
            <div className="flex items-start gap-3 mb-4">
              <FileArrowUp size={22} weight="bold" className="text-[hsl(var(--accent))]" />
              <div>
                <h2 className="heading text-lg tracking-tight">Add GST (optional)</h2>
                <p className="text-sm text-[hsl(var(--muted))] mt-1">Official GST Registration Certificate. Its PAN must match.</p>
              </div>
            </div>

            {gstConfirmed && !gstReview ? (
              <div className="border rounded-[2px] p-4 mb-3" data-testid="gst-confirmed">
                <div className="overline text-[10px]">GSTIN · CONFIRMED (READ-ONLY)</div>
                <div className="font-mono text-lg tracking-wide" data-testid="gstin-value">{flow.gstin_display}</div>
                <div className="flex gap-2 mt-3">
                  <button className="btn-ghost" disabled={busy} onClick={() => gstInput.current?.click()} data-testid="gst-replace-button">Replace document</button>
                  <button className="btn-ghost" disabled={busy} onClick={removeGst} data-testid="gst-remove-button">Remove</button>
                </div>
              </div>
            ) : gstReview ? (
              <div className="border rounded-[2px] p-4 mb-3" data-testid="gst-review">
                <div className="overline text-[10px]">EXTRACTED GSTIN · REVIEW (READ-ONLY)</div>
                <div className="font-mono text-lg tracking-wide" data-testid="gst-review-value">{gstReview.gstin_display}</div>
                {gstReview.pan_matches && (
                  <div className="text-xs text-emerald-700 flex items-center gap-1 mt-2" data-testid="gst-pan-match">
                    <CheckCircle size={13} weight="fill" /> Certificate PAN matches your company PAN.
                  </div>
                )}
                <p className="text-[11px] text-[hsl(var(--muted))] mt-3">If this is wrong, choose Replace document. GSTIN cannot be edited by hand.</p>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <button className="btn-ghost" disabled={busy} onClick={() => gstInput.current?.click()} data-testid="gst-replace-button">Replace document</button>
                  <button className="btn-ghost" disabled={busy} onClick={cancelGstReview} data-testid="gst-cancel-button">Cancel</button>
                  <button className="btn-primary ml-auto" disabled={busy} onClick={confirmGst} data-testid="gst-confirm-button">
                    <CheckCircle size={14} weight="bold" /> {busy ? "Working…" : "Confirm GSTIN"}
                  </button>
                </div>
              </div>
            ) : (
              <button className="btn-ghost w-full justify-center mb-3" disabled={busy} onClick={() => gstInput.current?.click()} data-testid="gst-upload-button">
                <FileArrowUp size={16} weight="bold" /> {busy ? "Processing…" : "Upload GST certificate"}
              </button>
            )}

            <div className="flex gap-2">
              <button className="btn-ghost" onClick={() => setStep("pan")} data-testid="gst-back-button"><ArrowLeft size={14} weight="bold" /> Back</button>
              {gstConfirmed && !gstReview ? (
                <button className="btn-primary ml-auto" disabled={busy} onClick={() => setStep("details")} data-testid="gst-continue-button">
                  Continue <ArrowRight size={14} weight="bold" />
                </button>
              ) : !gstReview ? (
                <button className="btn-primary ml-auto" disabled={busy} onClick={continueWithoutGst} data-testid="gst-skip-button">
                  Continue without GST <ArrowRight size={14} weight="bold" />
                </button>
              ) : null}
            </div>
            <input ref={gstInput} type="file" accept={ACCEPT} className="hidden"
                   onChange={(e) => reviewGst(e.target.files?.[0])} data-testid="gst-file-input" />
          </div>
        )}

        {/* STEP 3 — details / finalize */}
        {step === "details" && flow && (
          <div className="grid-card p-6" data-testid="onboarding-step-details">
            <div className="flex items-start gap-3 mb-4">
              <Buildings size={22} weight="bold" className="text-[hsl(var(--accent))]" />
              <div>
                <h2 className="heading text-lg tracking-tight">{flow.pan_exists ? "Join your company" : "Company details"}</h2>
                <p className="text-sm text-[hsl(var(--muted))] mt-1">PAN <span className="font-mono">{flow.pan_display}</span> · {flow.pan_holder_name}</p>
              </div>
            </div>

            {flow.pan_exists ? (
              <div className="border rounded-[2px] p-4 mb-4" data-testid="existing-company">
                <div className="overline text-[10px]">EXISTING COMPANY</div>
                <div className="text-sm font-semibold" data-testid="existing-company-name">{flow.existing_company?.legal_name}</div>
                <div className="text-sm text-[hsl(var(--muted))] mt-1 whitespace-pre-line" data-testid="existing-company-address">
                  {typeof flow.existing_company?.registered_address === "string"
                    ? flow.existing_company?.registered_address
                    : JSON.stringify(flow.existing_company?.registered_address)}
                </div>
                <p className="text-xs text-[hsl(var(--muted))] mt-3">This PAN already has an Air12 company. You’ll join it; its details are managed by the company.</p>
              </div>
            ) : (
              <div className="space-y-3 mb-4">
                <label className="block">
                  <div className="overline mb-1.5">COMPANY LEGAL / BUSINESS NAME</div>
                  <input className="input-field" value={name} onChange={(e) => setName(e.target.value)} data-testid="company-name-input" />
                </label>
                <label className="block">
                  <div className="overline mb-1.5">COMPANY ADDRESS</div>
                  <textarea className="input-field min-h-[90px]" value={address} onChange={(e) => setAddress(e.target.value)} data-testid="company-address-input" />
                </label>
                {flow.gst_present && (
                  <div className="text-xs text-[hsl(var(--muted))]" data-testid="gstin-readonly-note">
                    GSTIN <span className="font-mono">{flow.gstin_display}</span> will be recorded (read-only).
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <button className="btn-ghost" onClick={() => setStep("gst")} data-testid="details-back-button"><ArrowLeft size={14} weight="bold" /> Back</button>
              <button className="btn-primary ml-auto" disabled={busy} onClick={finalize}
                      data-testid={flow.pan_exists ? "join-company-button" : "complete-setup-button"}>
                <CheckCircle size={16} weight="bold" /> {busy ? "Working…" : (flow.pan_exists ? "Join Company" : "Continue")}
              </button>
            </div>
          </div>
        )}

        {/* STEP 4 — Air12 Sender Agreement (NEW company only; final mandatory gate) */}
        {step === "agreement" && flow && !flow.pan_exists && (
          <div className="grid-card p-6 space-y-4" data-testid="onboarding-step-agreement">
            <AgreementAcceptPanel agreement={agreement} loading={agreementLoading} error={agreementError}
                                  onRetry={loadAgreement} onAccept={acceptAgreement} busy={busy}
                                  testIdPrefix="onboarding-agreement" />
            <button className="btn-ghost" disabled={busy} onClick={() => setStep("details")} data-testid="agreement-back-button">
              <ArrowLeft size={14} weight="bold" /> Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
